<script setup lang="ts">
import type { CourseTeaser } from '~/composables/useCourse';
import { format } from 'date-fns';
import type { Speciality } from "@mui/types/courses";
import Arrow from '@/assets/images/illustrations/arrow.svg';

const props = withDefaults(defineProps<{
  course: CourseTeaser;
  updatedVersion?: boolean;
  sunsetAt?: string | null;
}>(), {
  updatedVersion: false,
  sunsetAt: null,
});

const { getImageUrl } = useCmsMedia();
const { t } = useI18n();

const isUpdatedVersion = computed(() => props.updatedVersion);
const hasSunsetDate = computed(() => props.sunsetAt !== null);

const formattedSunsetDate = computed(() => {
  return props.sunsetAt ? format(new Date(props.sunsetAt), "MMM do") : null;
});

const formattedSunsetDateLong = computed(() => {
  return props.sunsetAt ? format(new Date(props.sunsetAt), "MMMM do") : null;
});

const titleText = computed(() => {
  if (hasSunsetDate.value) {
    return t('course.relatedContent.sunsetTitle', {
      type: props.course?.type,
      date: formattedSunsetDate.value,
    });
  }

  if (isUpdatedVersion.value) {
    return t(`course.relatedContent.updatedTitle.${props.course?.type}`);
  }

  return t(`course.relatedContent.fallbackTitle.${props.course?.type}`);
});

const descriptionText = computed(() => {
  if (hasSunsetDate.value) {
    return t('course.relatedContent.sunsetDescription.1') + '<br/><br/>' +
     t('course.relatedContent.sunsetDescription.2', {
       date: `<strong>${formattedSunsetDateLong.value}</strong>`,
     });
  }

  if (isUpdatedVersion.value) {
    return t('course.relatedContent.updatedDescription');
  }

  return t(`course.relatedContent.fallbackDescription.${props.course?.type}`);
});
</script>

<template>
  <div class="course-related-content">
    <div class="column column--left">
      <div class="title-and-description">
        <h2 class="title">
          {{titleText}}
        </h2>
        <div
          v-sanitize-html="descriptionText"
          class="description"
        />
      </div>
      <div class="arrow">
        <Arrow />
      </div>
    </div>
    <div class="column column--right">
      <MCourseCard
        :id="course.id"
        :key="`course-card-${course.id}`"
        :cme-credits="course.cmeCredits"
        :duration-minutes="course.duration"
        :title="course.title"
        :target-url="`/courses/${course.id}`"
        :teachers="course.teachers"
        :specialization="[course.specialization as Speciality]"
        :background-image="getImageUrl(course.backgroundPattern)"
        :teacher-illustration="getImageUrl(course.teacherIllustration)"
        :progress="course.progress"
        :lessons="course.lessons"
        :quizzes="course.quizzes"
        :cases="course.cases"
        :type="course.type"
        :is-free-cme-course="course.isFreeCmeCourse"
        :available-languages="course.availableLanguages"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@mui/assets/styles/utility.scss';
@import '@mui/assets/styles/breakpoints.scss';
@import '@mui/assets/styles/font-styles.scss';

.course-related-content {
  @include container-sizer(page);

  display: grid;
  gap: 2em;
  grid-template-columns: 1fr;
  @include breakpoint('medium') {
    grid-template-columns: 1fr 1fr;
  }

  padding: 2em 1em;

  .column--left {
    display: grid;
    align-items: center;
    grid-template-columns: 1fr;
    @include breakpoint('medium') {
      grid-template-columns: 2fr 1fr;
    }

    .title-and-description {
      display: flex;
      flex-direction: column;
      align-items: center;
      @include breakpoint('medium') {
        align-items: flex-start;
      }

      .title {
        @include f-32-bold;
      }
      .description {
        @include f-18-regular;
      }
    }

    .arrow {
      display: none;
      @include breakpoint('medium') {
        display: flex;
        margin: 0 auto;
      }

      align-items: center;
      justify-content: flex-end;
    }
  }
}
</style>